import { useState, useEffect, useRef } from 'react';
import { Badge, Button, Card, Col, Form, ListGroup, Row, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import Rating from '../../components/ui/Rating';
import { useAddToCartMutation } from '../../slices/cartApiSlice';
import { useCreateReviewMutation, useGetProductDetailsQuery } from '../../slices/productApiSlice';
import { useCreateEventMutation } from '../../slices/productEventsApiSlice';
import { getSessionId } from '../../utils/sessionUtils';
import { bufferEvent } from '../../utils/eventBuffer';
import ProductImage from '../../components/product/ProductImage';
import '../../assets/styles/product.screen.css';
import ProductPrice from '../../components/product/ProductPrice';
import ProductDescription from '../../components/product/ProductDescription';
import VariationAvailabilityNotice from '../../components/product/VariationAvailabilityNotice';
import QuantitySelector from '../../components/ui/QuantitySelector';
import ContificoCodeBadge from '../../components/ui/ContificoCodeBadge';
import RelatedProductsCarousel from '../../components/product/RelatedProductsCarousel';
import {
    handleShare,
    handleWhatsApp,
    isLinkedVariation,
    buildLinkedVariationPicker,
    variationNeedsAvailabilityConfirmation,
} from '../../utils/productUtils';
import {
    getFormattedPriceParts,
    getProductPriceSummary,
} from '../../utils/priceUtils';
import { LuShare } from 'react-icons/lu';
import { FaWhatsapp, FaPencil, FaCartPlus, FaCheck, FaStore } from 'react-icons/fa6';
import '../../assets/styles/action-feedback.css';
import { feedbackClassName } from '../../hooks/useActionFeedback';
import { FaLock, FaFire, FaTag } from 'react-icons/fa';
import ProductEditScreen from '../admin/ProductEditScreen';
import { FreeShippingBadge } from '../../components/product/ProductBenefitMarks';

const LINKED_VARIATION_HINT_KEY = 'hs_linked_variation_hint';
const LINKED_VARIATION_ORDER_KEY = 'hs_linked_variation_order';

const ProductScreen = () => {
    const { _id: productId } = useParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { userInfo } = useSelector((state) => state.auth);
    const { activeOrder } = useSelector((state) => state.cart);
    const { companiesByRuc } = useSelector((state) => state.companies);

    const variationRef = useRef(null);

    const [qty, setQty] = useState(1);
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(null);
    const [totalStars] = useState(5);
    const [comment, setComment] = useState('');
    const [mainImage, setMainImage] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedVariations, setSelectedVariations] = useState({});
    const [linkedVariationOrderMap, setLinkedVariationOrderMap] = useState({});
    const [linkedMoveHint, setLinkedMoveHint] = useState({ variationType: null, optionLabel: '' });
    const productDetailsRef = useRef(null);

    const { data: product, refetch, isLoading, error } = useGetProductDetailsQuery(productId);
    const [addToCart] = useAddToCartMutation();
    const [createReview, { isLoading: isLoadingCreateReview }] = useCreateReviewMutation();
    const [createEvent] = useCreateEventMutation();

    const handleEditClose = () => {
        setShowEditModal(false);
        refetch();
    };

    const cProduct = product
        ? {
              id: product.contificoId,
              codigo: product.contificoCode,
              codigo_barra: product.contificoBarcode || '',
              nombre: product.name,
              descripcion: product.description,
              pvp1: product.price,
              pvp4: product.priceDist,
              porcentaje_iva: product.taxPercentage,
              cantidad_stock: product.countInStock,
              company: product.company,
              personalizado1: JSON.stringify({
                  onlinePvp: product.active,
                  onlinePvpDist: product.activeDist,
                  homeSantyDb: true,
              }),
          }
        : null;

    useEffect(() => {
        if (product && product._id) {
            setMainImage(product.image);
            setQty(1);
            setRating(0);
            setComment('');

            let persistedOrderMap = {};
            const rawLinkedOrder = sessionStorage.getItem(LINKED_VARIATION_ORDER_KEY);
            if (rawLinkedOrder) {
                try {
                    const parsedOrder = JSON.parse(rawLinkedOrder);
                    if (parsedOrder?.expiresAt > Date.now()) {
                        persistedOrderMap = parsedOrder.orderByVariationType || {};
                    }
                } catch (_error) {
                    // Ignore malformed order payload and continue with default behavior.
                }
            }
            
            // Initialize selectedVariations
            const initialVariations = {};
            const nextLinkedOrderMap = {};
            product.variations?.forEach((v) => {
                if (v.options?.length > 0) {
                    if (isLinkedVariation(v)) {
                        const picker = buildLinkedVariationPicker(
                            product,
                            v,
                            persistedOrderMap[v.variationType]
                        );
                        nextLinkedOrderMap[v.variationType] = picker.map((option) => option.id);
                        const currentOption = picker.find((option) => option.id === product._id);
                        initialVariations[v.variationType] = {
                            name: currentOption?.label || product.name,
                            linkedProduct: product._id,
                        };
                    } else {
                        initialVariations[v.variationType] = v.options[0];
                    }
                }
            });
            setSelectedVariations(initialVariations);
            setLinkedVariationOrderMap(nextLinkedOrderMap);
            sessionStorage.setItem(
                LINKED_VARIATION_ORDER_KEY,
                JSON.stringify({
                    orderByVariationType: nextLinkedOrderMap,
                    expiresAt: Date.now() + 30 * 60 * 1000,
                })
            );

            const rawLinkedHint = sessionStorage.getItem(LINKED_VARIATION_HINT_KEY);
            if (rawLinkedHint) {
                try {
                    const parsedHint = JSON.parse(rawLinkedHint);
                    if (parsedHint?.expiresAt > Date.now()) {
                        setLinkedMoveHint({
                            variationType: parsedHint.variationType || null,
                            optionLabel: parsedHint.optionLabel || '',
                        });
                    }
                } catch (_error) {
                    // Ignore malformed hint payload and continue with default behavior.
                }
                sessionStorage.removeItem(LINKED_VARIATION_HINT_KEY);
            }

            window.scrollTo(0, 0);
            bufferEvent(
                {
                    type: 'detail_view',
                    productId: product._id,
                    sessionId: getSessionId(),
                },
                createEvent
            );

            const queryParams = new URLSearchParams(search);
            if (queryParams.get('focus') === 'variations') {
                setTimeout(() => {
                    if (variationRef.current) {
                        variationRef.current.scrollIntoView({ behavior: 'smooth' });
                        const firstChip = variationRef.current.querySelector('.variation-chip-button');
                        if (firstChip) {
                            firstChip.focus();
                        }
                    }
                }, 100);
            }
        }
    }, [product, createEvent, search]);

    useEffect(() => {
        if (!linkedMoveHint.variationType) return;

        const clearHintTimer = setTimeout(() => {
            setLinkedMoveHint({ variationType: null, optionLabel: '' });
        }, 2200);

        return () => clearTimeout(clearHintTimer);
    }, [linkedMoveHint]);


    const showExtraDetails = userInfo?.priceAssigned === 'pvp_distribuidor';

    const countInStock = Number(product?.countInStock ?? 0);
    const unitsPerBox = Number(product?.unitsPerBox ?? 0);
    const inCarts = product?.inCarts || 0;

    const isLowStock =
        !!product &&
        (userInfo && userInfo?.priceAssigned === 'pvp_distribuidor'
            ? countInStock > 0 && countInStock <= 25
            : countInStock > 0 && countInStock <= 10);

    const isOutOfStock = !!product && countInStock === 0;

    const showUnitsPerBox =
        !!product &&
        Number.isFinite(unitsPerBox) &&
        unitsPerBox > 0 &&
        countInStock >= unitsPerBox * 1.5;

    const getSelectedVariationDetails = () => {
        const variationValues = Object.values(selectedVariations);
        if (variationValues.length === 0) return null;
        
        return variationValues[variationValues.length - 1];
    };

    const selectedVariation = getSelectedVariationDetails();

    const displayProduct = product || {};
    const defaultVariationImage = '/images/default.jpg';

    useEffect(() => {
        if (selectedVariation?.image) {
            setMainImage(selectedVariation.image);
        }
    }, [selectedVariation]);

    const displayImage = mainImage || '/images/default.jpg';

    const companyData = product?.company && typeof product.company === 'string' 
        ? companiesByRuc[product.company] 
        : product?.company;

    const populatedBrandData =
        product?.brandId && typeof product.brandId === 'object' ? product.brandId : null;
    const legacyBrandObject = product?.brand && typeof product.brand === 'object' ? product.brand : null;
    const productBrandData = populatedBrandData || legacyBrandObject;
    const productBrandName = productBrandData?.name || '';
    const productBrandImage = productBrandData?.image || productBrandData?.logo || null;
    const inlineBrandImage = productBrandData?.image || productBrandData?.logo || null;
    const rawBrandId = productBrandData?._id || (typeof product?.brandId === 'string' ? product.brandId : null);
    const productBrandId = rawBrandId ? String(rawBrandId) : '';
    const productBrandLink = productBrandId ? `/brand/${productBrandId}` : null;

    const companyRuc = companyData?.ruc || 'default';
    const isCompanyLocked = activeOrder?.companies?.[companyRuc]?.checkoutLocked || 
                           (Array.isArray(activeOrder?.companies) && activeOrder.companies.find(c => (c.company?.ruc || 'default') === companyRuc)?.checkoutLocked);

    const [addCartFeedback, setAddCartFeedback] = useState(null);
    const addCartFeedbackTimerRef = useRef(null);

    const flashAddCartFeedback = (status) => {
        setAddCartFeedback(status);
        if (addCartFeedbackTimerRef.current) {
            window.clearTimeout(addCartFeedbackTimerRef.current);
        }
        addCartFeedbackTimerRef.current = window.setTimeout(() => {
            setAddCartFeedback(null);
            addCartFeedbackTimerRef.current = null;
        }, 1400);
    };

    const addToCartHandler = async () => {
        if (!product || addCartFeedback === 'pending') return;
        if (isCompanyLocked) {
            flashAddCartFeedback('error');
            return;
        }

        const variationEntries = Object.entries(selectedVariations);
        let variationPayload = null;

        if (variationEntries.length > 0) {
            const firstNonLinkedVariation = variationEntries.find(([, option]) => !option?.linkedProduct);
            if (firstNonLinkedVariation) {
                const [firstType, firstOption] = firstNonLinkedVariation;
                variationPayload = {
                    variationType: firstType,
                    option: firstOption.name,
                    image: firstOption.image || null,
                };
            }
        }

        const itemToAdd = {
            productId,
            qty,
            variation: variationPayload,
        };

        try {
            setAddCartFeedback('pending');
            await addToCart(itemToAdd).unwrap();
            flashAddCartFeedback('success');
        } catch {
            flashAddCartFeedback('error');
        }
    };

    const submitReviewHandler = async (e) => {
        e.preventDefault();
        try {
            await createReview({ productId, rating, comment }).unwrap();
            refetch();
            toast.success('Reseña guardada');
            setRating(0);
            setComment('');
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    return (
        <>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : (
                <>
                    <Meta title={product.name} />
                    <Row className="g-4 product-screen-main-row">
                        <Col lg={5} md={12}>
                            <div
                                className="product-screen-image-wrapper"
                                style={{ position: 'relative' }}
                            >

                                {inCarts > 0 && (
                                    <div className="product-screen-hot-badge">
                                        🔥 En {inCarts} {inCarts === 1 ? 'carrito' : 'carritos'}
                                    </div>
                                )}

                                {isLowStock && (
                                    <div className="product-screen-low-stock-tag">Bajo Stock</div>
                                )}

                                {isOutOfStock && (
                                    <div className="product-screen-out-of-stock-tag">Agotado</div>
                                )}

                                <ProductImage
                                    src={displayImage}
                                    images={[product.image, ...(product.images || [])]}
                                    alt={product.name}
                                    zoomTargetRef={productDetailsRef}
                                />
                            </div>
                        </Col>

                        <Col lg={4} md={7} className="product-screen-info-col" ref={productDetailsRef}>
                            <ListGroup variant="flush" className="border-0">
                                <ListGroup.Item className="border-0 pb-2">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="d-flex flex-column">
                                            <h2 className="product-screen-name fw-bold mb-0">{product.name}</h2>
                                            <FreeShippingBadge product={product} />
                                            {productBrandName && (
                                                <div className="product-screen-inline-brand-wrap">
                                                    {productBrandLink ? (
                                                        <Link to={productBrandLink} className="product-screen-inline-brand-chip">
                                                            {inlineBrandImage ? (
                                                                <img
                                                                    src={inlineBrandImage}
                                                                    alt={productBrandName}
                                                                    className="product-screen-inline-brand-avatar"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        e.currentTarget.nextElementSibling?.style.setProperty(
                                                                            'display',
                                                                            'inline-flex'
                                                                        );
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <span
                                                                className="product-screen-inline-brand-avatar-fallback"
                                                                aria-hidden="true"
                                                                style={{ display: inlineBrandImage ? 'none' : 'inline-flex' }}
                                                            >
                                                                <FaTag />
                                                            </span>
                                                            <span>{productBrandName}</span>
                                                        </Link>
                                                    ) : (
                                                        <span className="product-screen-inline-brand-chip is-static">
                                                            {inlineBrandImage ? (
                                                                <img
                                                                    src={inlineBrandImage}
                                                                    alt={productBrandName}
                                                                    className="product-screen-inline-brand-avatar"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        e.currentTarget.nextElementSibling?.style.setProperty(
                                                                            'display',
                                                                            'inline-flex'
                                                                        );
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <span
                                                                className="product-screen-inline-brand-avatar-fallback"
                                                                aria-hidden="true"
                                                                style={{ display: inlineBrandImage ? 'none' : 'inline-flex' }}
                                                            >
                                                                <FaTag />
                                                            </span>
                                                            <span>{productBrandName}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {companyData && (
                                                <div className="product-screen-seller-row">
                                                    <Link
                                                        to={`/company/${companyData.ruc}`}
                                                        className="seller-chip seller-chip--badge"
                                                    >
                                                        <FaStore size={10} />
                                                        <span>
                                                            {companyData.tradeName}
                                                            {companyData.city && ` · ${companyData.city}`}
                                                        </span>
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                        {userInfo && userInfo.isAdmin && (
                                            <Button
                                                variant="light"
                                                className="btn-sm ms-2 shadow-sm rounded-circle"
                                                onClick={() => setShowEditModal(true)}
                                                style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <FaPencil size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 py-1">
                                    <div className="product-screen-meta-row">
                                        <Rating
                                            value={product.rating}
                                            text={`${product.numReviews} reseña(s)`}
                                            size="lg"
                                            className="product-screen-main-rating"
                                        />
                                    </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="product-screen-price-section border-0 py-3">
                                    <ProductPrice product={displayProduct} userInfo={userInfo} size="xl" />
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0">
                                    {product.variations?.length > 0 && (
                                        <div ref={variationRef} className="product-screen-variation-section mt-3">
                                            {product.variations.map((v, idx) => {
                                                const linkedMode = isLinkedVariation(v);
                                                const linkedPickerOptions = linkedMode
                                                    ? buildLinkedVariationPicker(
                                                        product,
                                                        v,
                                                        linkedVariationOrderMap[v.variationType]
                                                    ).map((opt) => {
                                                          const linkedSource = v.options.find(
                                                              (option) => option.linkedProduct === opt.id
                                                          );
                                                          const linkedOptionPricingData =
                                                              linkedSource &&
                                                              (linkedSource.price !== undefined ||
                                                                  linkedSource.priceDist !== undefined ||
                                                                  linkedSource.discount !== undefined ||
                                                                  linkedSource.appliedDiscountPercentage !== undefined)
                                                                  ? {
                                                                        price: linkedSource.price,
                                                                        priceDist: linkedSource.priceDist,
                                                                        taxPercentage:
                                                                            linkedSource.taxPercentage ?? product.taxPercentage,
                                                                        discount: linkedSource.discount,
                                                                        appliedDiscountPercentage:
                                                                            linkedSource.appliedDiscountPercentage,
                                                                        discountDateFrom: linkedSource.discountDateFrom,
                                                                        discountDateTo: linkedSource.discountDateTo,
                                                                    }
                                                                  : null;
                                                          const linkedProductData = opt.id === product._id
                                                              ? product
                                                              : (typeof linkedSource?.linkedProduct === 'object'
                                                                  ? linkedSource.linkedProduct
                                                                  : linkedOptionPricingData);
                                                          const linkedPriceValue = linkedProductData
                                                              ? getProductPriceSummary(linkedProductData, userInfo).finalPrice
                                                              : null;
                                                          const linkedPriceParts = linkedPriceValue !== null
                                                              ? getFormattedPriceParts(linkedPriceValue)
                                                              : null;
                                                          return {
                                                              ...opt,
                                                              image: opt.id === product._id ? product.image : linkedSource?.image,
                                                              linkedPriceParts,
                                                          };
                                                      })
                                                    : [];
                                                const selectedValue = linkedMode
                                                    ? selectedVariations[v.variationType]?.linkedProduct || product._id
                                                    : selectedVariations[v.variationType]?.name || '';
                                                const shouldAnimateLinkedMove =
                                                    linkedMode && linkedMoveHint.variationType === v.variationType;

                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`variation-chip-group mb-3 ${shouldAnimateLinkedMove ? 'linked-move-focus' : ''}`}
                                                    >
                                                        <div className="variation-chip-group-label">{v.variationType}:</div>
                                                        <div
                                                            className="variation-chip-list"
                                                            role="radiogroup"
                                                            aria-label={`Seleccionar ${v.variationType}`}
                                                        >
                                                            {(linkedMode ? linkedPickerOptions : v.options).map((opt, oIdx) => {
                                                                const value = linkedMode ? opt.id : opt.name;
                                                                const isSelected = value === selectedValue;
                                                                const optionLabel = linkedMode ? opt.label : opt.name;
                                                                const optionImage = linkedMode ? opt.image : opt.image;
                                                                const hasImage = !!optionImage;
                                                                const optionInitials = (optionLabel || '')
                                                                    .split(' ')
                                                                    .filter(Boolean)
                                                                    .slice(0, 2)
                                                                    .map((part) => part[0])
                                                                    .join('')
                                                                    .toUpperCase() || '?';

                                                                return (
                                                                    <button
                                                                        key={linkedMode ? `${opt.id}-${oIdx}` : `${opt.name}-${oIdx}`}
                                                                        type="button"
                                                                        className={`variation-chip-button ${hasImage ? '' : 'no-image'} ${isSelected ? 'active' : ''}`}
                                                                        role="radio"
                                                                        aria-checked={isSelected}
                                                                        onClick={() => {
                                                                            if (linkedMode) {
                                                                                const selectedId = opt.id;
                                                                                const selectedOption = {
                                                                                    name: opt.label || product.name,
                                                                                    linkedProduct: selectedId,
                                                                                };

                                                                                setSelectedVariations((prev) => ({
                                                                                    ...prev,
                                                                                    [v.variationType]: selectedOption,
                                                                                }));

                                                                                if (selectedId !== product._id) {
                                                                                    const nextOrderByVariationType = {
                                                                                        ...linkedVariationOrderMap,
                                                                                        [v.variationType]:
                                                                                            linkedVariationOrderMap[v.variationType]?.length
                                                                                                ? linkedVariationOrderMap[v.variationType]
                                                                                                : linkedPickerOptions.map((option) => option.id),
                                                                                    };
                                                                                    sessionStorage.setItem(
                                                                                        LINKED_VARIATION_HINT_KEY,
                                                                                        JSON.stringify({
                                                                                            variationType: v.variationType,
                                                                                            optionLabel: opt.label || product.name,
                                                                                            expiresAt: Date.now() + 10000,
                                                                                        })
                                                                                    );
                                                                                    sessionStorage.setItem(
                                                                                        LINKED_VARIATION_ORDER_KEY,
                                                                                        JSON.stringify({
                                                                                            orderByVariationType: nextOrderByVariationType,
                                                                                            expiresAt: Date.now() + 30 * 60 * 1000,
                                                                                        })
                                                                                    );
                                                                                    navigate(`/product/${selectedId}`);
                                                                                }
                                                                                return;
                                                                            }

                                                                            const selectedOption = v.options.find(
                                                                                (option) => option.name === opt.name
                                                                            );
                                                                            setSelectedVariations((prev) => ({
                                                                                ...prev,
                                                                                [v.variationType]: selectedOption,
                                                                            }));
                                                                            if (selectedOption?.image) {
                                                                                setMainImage(selectedOption.image);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {hasImage ? (
                                                                            <img
                                                                                src={optionImage}
                                                                                alt={optionLabel}
                                                                                className="variation-chip-image"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.onerror = null;
                                                                                    e.currentTarget.src = defaultVariationImage;
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <span className="variation-chip-fallback" aria-hidden="true">
                                                                                {optionInitials}
                                                                            </span>
                                                                        )}
                                                                        <span className="variation-chip-content">
                                                                            <span className="variation-chip-text">{optionLabel}</span>
                                                                            {linkedMode && opt.linkedPriceParts && (
                                                                                <span className="variation-chip-price">
                                                                                    ${opt.linkedPriceParts[0]}.{opt.linkedPriceParts[1]}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {variationNeedsAvailabilityConfirmation(v) && (
                                                            <VariationAvailabilityNotice
                                                                product={product}
                                                                variationType={v.variationType}
                                                                selectedVariation={selectedVariations[v.variationType]}
                                                                title={v.variationType}
                                                                description="Esta variacion puede cambiar segun disponibilidad. Confirmanos antes de finalizar."
                                                                cardClassName="variation-availability-card--screen"
                                                                buttonClassName="product-screen-mini-btn px-3 py-1"
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <ProductDescription description={product.description} />
                                    <ContificoCodeBadge
                                        code={product?.contificoCode}
                                        className="mt-3"
                                    />
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>

                        <Col lg={3} md={5} className="product-screen-summary-col">
                            <Card className="p-2 p-md-3 product-screen-cta-card shadow-sm border-0">
                                <ListGroup variant="flush">
                                    <ListGroup.Item className="d-none d-md-block border-0 pb-0">
                                        <Row className="align-items-center">
                                            <Col xs={4} md={5} className="text-muted product-screen-cta-label">Precio:</Col>
                                            <Col xs={8} md={7} className="text-end d-flex justify-content-end">
                                                <ProductPrice
                                                    product={displayProduct}
                                                    userInfo={userInfo}
                                                    size="lg"
                                                    className="justify-content-end flex-column"
                                                />
                                            </Col>
                                        </Row>
                                    </ListGroup.Item>

                                    <ListGroup.Item className="border-0 py-2">
                                        <Row className="align-items-start">
                                            <Col xs={4} md={5} className="text-muted product-screen-cta-label">Categorías:</Col>
                                            <Col
                                                xs={8}
                                                md={7}
                                                className="d-flex flex-wrap gap-1 justify-content-end product-screen-category-col"
                                            >
                                                {product.category?.map((cat, index) => (
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                        key={index}
                                                        className="border product-screen-category-badge"
                                                        title={cat.name}
                                                    >
                                                        {cat.name}
                                                    </Badge>
                                                ))}
                                            </Col>
                                        </Row>
                                    </ListGroup.Item>

                                    {productBrandName && (
                                        <ListGroup.Item className="d-none d-md-block border-0 py-2">
                                            <Row className="align-items-center">
                                                <Col xs={4} md={5} className="text-muted product-screen-cta-label">Marca:</Col>
                                                <Col xs={8} md={7} className="text-end">
                                                    {productBrandLink ? (
                                                        <Link to={productBrandLink} className="product-screen-brand-badge product-screen-brand-badge-link">
                                                            {productBrandImage ? (
                                                                <img
                                                                    src={productBrandImage}
                                                                    alt={productBrandName}
                                                                    className="product-screen-brand-logo"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="product-screen-brand-fallback" aria-hidden="true">
                                                                    <FaTag />
                                                                </span>
                                                            )}
                                                            <span className="product-screen-brand-name">
                                                                {productBrandName}
                                                            </span>
                                                        </Link>
                                                    ) : (
                                                        <div className="product-screen-brand-badge">
                                                            {productBrandImage ? (
                                                                <img
                                                                    src={productBrandImage}
                                                                    alt={productBrandName}
                                                                    className="product-screen-brand-logo"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="product-screen-brand-fallback" aria-hidden="true">
                                                                    <FaTag />
                                                                </span>
                                                            )}
                                                            <span className="product-screen-brand-name">
                                                                {productBrandName}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    )}

                                    <ListGroup.Item className="border-0 py-2">
                                        <Row className="align-items-center">
                                            <Col xs={4} md={5} className="text-muted product-screen-cta-label">Estado:</Col>
                                            <Col xs={8} md={7} className="text-end">
                                                <div className="d-flex flex-column align-items-end gap-1">
                                                    <strong>
                                                        {displayProduct?.countInStock > 0 ? (
                                                            <Badge bg="success" className="px-3">
                                                                Disponible
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="danger" className="px-3">
                                                                Agotado
                                                            </Badge>
                                                        )}
                                                    </strong>
                                                    {isLowStock && (
                                                        <div className="low-stock-text">
                                                            ¡Solo {displayProduct.countInStock} {displayProduct.countInStock === 1 ? 'disponible' : 'disponibles'}!
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </ListGroup.Item>

                                    {inCarts > 0 && (
                                        <ListGroup.Item className="border-0 py-2">
                                            <div className="d-flex align-items-center justify-content-center gap-2 p-2 rounded bg-light text-danger fw-bold border border-danger-subtle product-screen-cta-note">
                                                <FaFire />
                                                {inCarts} {inCarts === 1 ? 'persona tiene' : 'personas tienen'} esto en su carrito
                                            </div>
                                        </ListGroup.Item>
                                    )}


                                    {showExtraDetails && (
                                        <ListGroup.Item className="border-0 py-2">
                                            <div className="product-screen-distributor-info-row">
                                                <div className="product-screen-stock-info">
                                                    <div className="product-screen-stock-content">
                                                        <div className="product-screen-stock-label">Stock Disponible</div>
                                                        <div className="product-screen-stock-value">{displayProduct?.countInStock}</div>
                                                    </div>
                                                    <div className={`product-screen-stock-indicator ${
                                                        displayProduct?.countInStock > 50 ? 'stock-high' :
                                                        displayProduct?.countInStock > 25 ? 'stock-medium' :
                                                        displayProduct?.countInStock > 0 ? 'stock-low' : 'stock-out'
                                                    }`}></div>
                                                </div>

                                                {showUnitsPerBox && (
                                                    <div className="product-screen-units-info">
                                                        <div className="product-screen-units-content">
                                                            <div className="product-screen-units-label">Unidades x Caja</div>
                                                            <div className="product-screen-units-value">{product.unitsPerBox}</div>
                                                        </div>
                                                        <div className="product-screen-units-icon">📦</div>
                                                    </div>
                                                )}
                                            </div>
                                        </ListGroup.Item>
                                    )}

                                    {displayProduct?.countInStock > 0 && (
                                        <ListGroup.Item className="border-0 py-2">
                                            <Form.Group controlId="quantity">
                                                <Row className="align-items-center">
                                                    <Col xs={4} md={5} className="text-muted product-screen-cta-label">Cantidad:</Col>
                                                    <Col xs={8} md={7} className="d-flex justify-content-end">
                                                        <QuantitySelector
                                                            value={qty}
                                                            max={displayProduct?.countInStock}
                                                            onChange={(val) => setQty(val)}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Form.Group>
                                        </ListGroup.Item>
                                    )}

                                    <ListGroup.Item className="d-grid border-0 pt-3 px-2 px-md-3">
                                        <Button
                                            type="button"
                                            disabled={product.countInStock === 0 || isCompanyLocked || addCartFeedback === 'pending' || addCartFeedback === 'success'}
                                            onClick={addToCartHandler}
                                            className={`add-to-cart-btn shadow-sm py-2 py-md-3 ${addCartFeedback === 'success' ? 'is-added' : ''} ${feedbackClassName(addCartFeedback)}`}
                                            style={{ borderRadius: '12px', fontWeight: '600' }}
                                        >
                                            {addCartFeedback === 'success' ? <FaCheck className="me-2" /> : <FaCartPlus className="me-2" />}
                                            {isCompanyLocked ? 'Pago en Proceso' : addCartFeedback === 'success' ? 'Agregado' : 'Agregar al Carrito'}
                                        </Button>
                                        {isCompanyLocked && (
                                            <div className="text-warning small text-center mt-2 fw-bold">
                                                <FaLock className="me-1" /> Pago en proceso para esta compañía
                                            </div>
                                        )}
                                    </ListGroup.Item>
                                    <ListGroup.Item className="d-flex justify-content-between gap-2 border-0 px-2 px-md-3 pb-3">
                                        <button
                                            onClick={() => handleShare(product)}
                                            className="btn btn-light d-flex align-items-center justify-content-center flex-grow-1 py-2 border product-screen-action-btn"
                                            style={{ borderRadius: '10px' }}
                                            type="button"
                                        >
                                            <LuShare size={16} className="me-2" />
                                            Compartir
                                        </button>

                                        <button
                                            onClick={() => handleWhatsApp(product, 'help')}
                                            className="btn btn-outline-success d-flex align-items-center justify-content-center flex-grow-1 py-2 product-screen-action-btn"
                                            style={{ borderRadius: '10px' }}
                                            type="button"
                                        >
                                            <FaWhatsapp size={16} className="me-2" />
                                            Ayuda
                                        </button>
                                    </ListGroup.Item>
                                </ListGroup>
                            </Card>
                        </Col>
                    </Row>

                    <RelatedProductsCarousel productId={productId} />

                    <Row className="review product-screen-reviews mt-5">
                        <Col md={6}>
                            <h2 className="mb-4">Reseñas</h2>

                             {product.reviews.length === 0 ? (
                                 <Message>No existen reseñas</Message>
                             ) : (
                                 <div className="product-screen-reviews-list">
                                     {product.reviews.map((review) => (
                                         <div key={review._id} className="product-screen-review-card">
                                             <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                 <strong className="product-screen-reviewer-name">{review.name}</strong>
                                                 {review.verified && (
                                                     <span className="product-screen-verified-badge">Verificado</span>
                                                 )}
                                             </div>
                                             <Rating value={review.rating} size="md" className="product-screen-review-rating" />
                                             <small className="text-muted d-block mb-1">
                                                 {review.createdAt.substring(0, 10)}
                                             </small>
                                             <p className="mt-2 mb-0 product-screen-review-comment">{review.comment}</p>
                                         </div>
                                     ))}
                                 </div>
                             )}

                            <ListGroup className="mt-5">
                                <ListGroup.Item>
                                    <h2>Dejar una reseña</h2>
                                    {isLoadingCreateReview && <Loader />}
                                    {userInfo ? (
                                        <Form onSubmit={submitReviewHandler}>
                                            <Form.Group
                                                controlId="rating"
                                                className="my-3 text-center"
                                            >
                                                <div className="review-rating-picker" role="radiogroup" aria-label="Calificacion">
                                                {[...Array(totalStars)].map((_, index) => {
                                                    const currentRating = index + 1;
                                                    return (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className={`review-star-btn ${currentRating <= (hover || rating) ? 'is-active' : ''}`}
                                                            onMouseEnter={() => setHover(currentRating)}
                                                            onMouseLeave={() => setHover(null)}
                                                            onClick={() => setRating(currentRating)}
                                                            aria-label={`${currentRating} estrella${currentRating > 1 ? 's' : ''}`}
                                                            aria-checked={rating === currentRating}
                                                            role="radio"
                                                        >
                                                            &#9733;
                                                        </button>
                                                    );
                                                })}
                                                </div>
                                                <div className="mt-2">
                                                    {rating === 1 && 'Poco Aceptable'}
                                                    {rating === 2 && 'Aceptable'}
                                                    {rating === 3 && 'Bueno'}
                                                    {rating === 4 && 'Muy Bueno'}
                                                    {rating === 5 && 'Excelente!'}
                                                </div>
                                            </Form.Group>

                                            <Form.Group controlId="comment" className="my-3">
                                                <Form.Label column='lg'>Comentario</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                />
                                            </Form.Group>

                                            <Button
                                                type="submit"
                                                disabled={isLoadingCreateReview}
                                                variant="primary"
                                            >
                                                Calificar
                                            </Button>
                                        </Form>
                                    ) : (
                                        <Message>
                                            Por favor <Link to="/login">inicie sesión</Link> para
                                            dejar una reseña
                                        </Message>
                                    )}
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>
                    </Row>
                </>
            )}
            <Modal show={showEditModal} onHide={handleEditClose} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Editar Producto</Modal.Title>
                </Modal.Header>
                <ProductEditScreen
                    cProduct={cProduct}
                    handleClose={handleEditClose}
                    refetch={refetch}
                />
            </Modal>
        </>
    );
};

export default ProductScreen;
